import { useEffect } from "react";
import ChatWidget from "@/components/ChatWidget";

/**
 * Standalone embeddable chat page for Wix / external sites.
 * - Body is fully transparent so only the floating chat bubble/panel show.
 * - Hides the global "Made with Emergent" badge so it doesn't clutter
 *   the host page's bottom-right corner.
 * - Posts an "open" / "close" message to the parent window whenever the
 *   chat panel opens/closes so the host can dynamically resize the iframe
 *   (tiny when closed = just the bubble, full when open = chat panel).
 *
 * Security:
 *   postMessage targets are restricted to an allow-list — the Freedom In Me
 *   marketing domains (Wix-hosted and the standalone deploy). Override with
 *   `REACT_APP_EMBED_ALLOWED_ORIGINS` (comma-separated) for additional hosts.
 */
const DEFAULT_ALLOWED_ORIGINS = [
  "https://www.itsthefreedominme.com",
  "https://itsthefreedominme.com",
  "https://align.itsthefreedominme.com",
];

function getAllowedOrigins() {
  const env = process.env.REACT_APP_EMBED_ALLOWED_ORIGINS || "";
  const extras = env
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  return Array.from(new Set([...DEFAULT_ALLOWED_ORIGINS, ...extras]));
}

export default function EmbedChat() {
  useEffect(() => {
    // Hide the platform badge while this embed page is mounted
    const badge = document.getElementById("emergent-badge");
    const prevDisplay = badge ? badge.style.display : null;
    if (badge) badge.style.display = "none";

    const allowedOrigins = getAllowedOrigins();

    // Tell every ancestor window we've loaded so the host can resize
    // the iframe. We broadcast to both `parent` and `top` because some
    // hosts (notably Wix Custom Code) nest the embed inside their own
    // sandboxed iframe — in that case `parent` is the wrapper iframe
    // and `top` is the actual host page.
    //
    // We post once per allowed origin (rather than `'*'`) so a malicious
    // host page can't read the message contents.
    const post = (state) => {
      const msg = { source: "align-chat", state };
      const targets = new Set();
      try {
        if (window.parent && window.parent !== window) targets.add(window.parent);
      } catch (err) {
        // Cross-origin access to window.parent is expected to throw in some sandboxed contexts.
        console.debug("[embed-chat] window.parent inaccessible:", err?.message);
      }
      try {
        if (window.top && window.top !== window) targets.add(window.top);
      } catch (err) {
        console.debug("[embed-chat] window.top inaccessible:", err?.message);
      }
      targets.forEach((t) => {
        allowedOrigins.forEach((origin) => {
          try {
            t.postMessage(msg, origin);
          } catch (err) {
            // postMessage can throw if the target is detached or the origin is blocked.
            console.debug(`[embed-chat] postMessage to ${origin} failed:`, err?.message);
          }
        });
      });
    };

    post("close");

    // Watch the DOM for the chat panel appearing/disappearing.
    const observer = new MutationObserver(() => {
      const panel = document.querySelector('[data-testid="chat-panel"]');
      post(panel ? "open" : "close");
    });
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      if (badge) badge.style.display = prevDisplay || "";
    };
  }, []);

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "transparent",
        pointerEvents: "none",
      }}
      data-testid="embed-chat-root"
    >
      {/* Re-enable pointer events only on the chat itself */}
      <div style={{ pointerEvents: "auto" }}>
        <ChatWidget />
      </div>
    </div>
  );
}
